import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, Search, X } from "lucide-react";
import { listAthletes, createAthlete } from "../lib/api";
import { BRAND } from "../lib/constants";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    // Date-only strings (YYYY-MM-DD) get constructed in LOCAL time so they
    // render as the same day the coach entered. `new Date("2026-05-15")`
    // parses as UTC midnight, which displays as May 14 in MT/PT.
    const m = typeof iso === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function AthletesPage() {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent_test");
  const navigate = useNavigate();

  const q = searchTerm.trim().toLowerCase();
  const filteredAthletes = q
    ? athletes.filter(
        (a) =>
          (a.name || "").toLowerCase().includes(q) ||
          (a.email || "").toLowerCase().includes(q)
      )
    : athletes;

  const sortedAthletes = React.useMemo(() => {
    const list = [...filteredAthletes];
    const byNameAsc = (a, b) => (a.name || "").localeCompare(b.name || "");
    if (sortBy === "name_asc") list.sort(byNameAsc);
    else if (sortBy === "name_desc") list.sort((a, b) => byNameAsc(b, a));
    else if (sortBy === "added_desc")
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sortBy === "added_asc")
      list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sortBy === "recent_test")
      // newest test first; athletes with no tests fall to the bottom
      list.sort((a, b) => (b.last_test_date || "").localeCompare(a.last_test_date || ""));
    else if (sortBy === "oldest_test")
      // oldest test first; athletes with no tests fall to the bottom
      list.sort((a, b) => {
        if (!a.last_test_date && !b.last_test_date) return 0;
        if (!a.last_test_date) return 1;
        if (!b.last_test_date) return -1;
        return (a.last_test_date || "").localeCompare(b.last_test_date || "");
      });
    return list;
  }, [filteredAthletes, sortBy]);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listAthletes();
      setAthletes(data);
    } catch (e) {
      toast.error("Couldn't load athletes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const a = await createAthlete(form);
      toast.success(`Added ${a.name}`);
      setShowAdd(false);
      setForm({ name: "", email: "", phone: "", notes: "" });
      refresh();
    } catch (err) {
      toast.error("Failed to add athlete");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="athletes-page">
      {/* Hero */}
      <section className="mb-14 border-b border-[var(--tir-ink)] pb-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-4">
              Lactate Profiling · Studio
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] text-[var(--tir-ink)]">
              Build lactate reports your runners actually read.
            </h1>
            <p className="text-base text-[var(--tir-muted)] mt-5 leading-relaxed max-w-xl">
              Input your testing data, mark LT1 and LT2, prescribe zones, and export a
              print-ready PDF that matches your brand.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAdd(true)}
              data-testid="add-athlete-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-6 py-3 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              <Plus size={16} /> Add athlete
            </button>
          </div>
        </div>
      </section>

      {/* Add form */}
      {showAdd && (
        <form
          onSubmit={submit}
          className="mb-10 border border-[var(--tir-ink)] bg-white p-6"
          data-testid="add-athlete-form"
        >
          <div className="font-serif-display text-2xl mb-5">New athlete</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Name
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
                data-testid="athlete-name-input"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Email (optional)
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
                data-testid="athlete-email-input"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Phone (optional)
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none font-mono-tir"
                data-testid="athlete-phone-input"
                placeholder="+1 555-555-5555"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Notes
              </label>
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
                data-testid="athlete-notes-input"
                placeholder="Marathon PB 3:05"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-athlete-btn"
              className="bg-[var(--tir-burgundy)] text-white px-6 py-2.5 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Save athlete"}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="text-sm tracking-wider uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
              data-testid="cancel-athlete-btn"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* List */}
      <section>
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-serif-display text-3xl">Your athletes</h2>
          <span className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)]" data-testid="athletes-count">
            {q ? `${filteredAthletes.length} of ${athletes.length}` : `${athletes.length} total`}
          </span>
        </div>

        {athletes.length > 0 && (
          <div
            className="flex flex-col md:flex-row md:items-end gap-3 md:gap-6 mb-6"
            data-testid="athlete-toolbar"
          >
            <div className="relative flex-1" data-testid="athlete-search-container">
              <Search
                size={16}
                className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--tir-muted)] pointer-events-none"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email"
                data-testid="athlete-search-input"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] pl-7 pr-8 py-2.5 text-base focus:outline-none placeholder:text-[var(--tir-muted)]"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  data-testid="athlete-search-clear"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <div className="md:w-72 shrink-0">
              <label
                htmlFor="athlete-sort-select"
                className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1"
              >
                Sort by
              </label>
              <select
                id="athlete-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="athlete-sort-select"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 text-base focus:outline-none cursor-pointer"
              >
                <option value="recent_test">Recent test (newest first)</option>
                <option value="oldest_test">Recent test (oldest first)</option>
                <option value="name_asc">Name (A → Z)</option>
                <option value="name_desc">Name (Z → A)</option>
                <option value="added_desc">Date added (newest first)</option>
                <option value="added_asc">Date added (oldest first)</option>
              </select>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-[var(--tir-muted)] py-12 text-center">Loading…</div>
        ) : athletes.length === 0 ? (
          <div className="border border-dashed border-[var(--tir-border)] py-16 text-center">
            <img
              src={BRAND.logoUrl}
              alt={`${BRAND.name} logo`}
              className="mx-auto mb-4 h-14 w-auto object-contain opacity-70"
              style={{ filter: "invert(1)" }}
              data-testid="empty-state-logo"
            />
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">No athletes yet</div>
            <div className="text-sm text-[var(--tir-muted)] mt-2">
              Add your first runner to start logging lactate tests.
            </div>
          </div>
        ) : filteredAthletes.length === 0 ? (
          <div
            className="border border-dashed border-[var(--tir-border)] py-12 text-center"
            data-testid="athlete-search-empty"
          >
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              No athletes match "{searchTerm}"
            </div>
            <div className="text-sm text-[var(--tir-muted)] mt-2">
              Try a different name or email.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--tir-border)] border-t border-b border-[var(--tir-border)]">
            {sortedAthletes.map((a) => (
              <li key={a.id} data-testid={`athlete-row-${a.id}`}>
                <Link
                  to={`/athletes/${a.id}`}
                  className="grid grid-cols-12 gap-4 py-5 px-2 hover:bg-white transition-colors group"
                >
                  <div className="col-span-12 md:col-span-5">
                    <div className="font-serif-display text-2xl text-[var(--tir-ink)] group-hover:text-[var(--tir-burgundy)] transition-colors">
                      {a.name}
                    </div>
                    {a.email && (
                      <div className="text-sm text-[var(--tir-muted)] font-mono-tir mt-0.5">
                        {a.email}
                      </div>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-3 text-sm text-[var(--tir-muted)]">
                    <div className="text-[10px] tracking-[0.22em] uppercase">Last test</div>
                    <div data-testid={`athlete-last-test-${a.id}`}>
                      {a.last_test_date ? (
                        <>
                          {formatDate(a.last_test_date)}
                          {a.test_count > 1 && (
                            <span className="text-[var(--tir-muted)]"> · {a.test_count} total</span>
                          )}
                        </>
                      ) : (
                        <span className="italic text-[var(--tir-muted)]">No tests yet</span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-3 text-sm text-[var(--tir-muted)]">
                    <div className="text-[10px] tracking-[0.22em] uppercase">Added</div>
                    <div>{formatDate(a.created_at)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-1 flex items-center md:justify-end text-[var(--tir-burgundy)]">
                    <ArrowUpRight
                      size={20}
                      className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                    />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
